import {action, computed, observable, runInAction} from 'mobx';
import mainStore from '../containers/main/store';
import appStore from '../containers/app/store';
import projectStore from '../containers/main/project/store';
import request from '../utils/request';
import dialogStore from '../containers/dialog/store';
import {FOLDER} from '../constants/urlConstants';
import {getId, getNewLessonParams, isSchoolAdmin} from '../utils/userUtility';
import searchStore from './SearchStore';
import {actions, AlertType, category, events} from "../constants/enums";
import {customEvent} from "../utils/analyticsUtil";

class FolderStore {
    @observable folders = [];
    @observable editedFolder = null;
    @observable activeFolder = null;

    foldersFetched = false;

    @action openFolderNameDialog = (folder = null, shared = false) => {
        dialogStore.openSetFolderNameDialog();
        this.editedFolder = folder || {name: '', shared: shared};
    };

    @action closeFolderNameDialog = () => {
        dialogStore.closeSetFolderNameDialog();
        this.editedFolder = null;
    };

    @action setActiveFolder = async (id) => {
        if (!this.foldersFetched) {
            await this.fetchFolders();
        }
        runInAction(() => {
            this.activeFolder = this.folders.find(folder => folder.id === id);
        });
    };

    @action unsetActiveFolder = () => {
        this.activeFolder = null;
    };

    @action editFolderName = (name, value = '') => {
        this.editedFolder.name = value;
    };

    @computed
    get folderId() {
        return this.activeFolder ? this.activeFolder.id : null;
    }

    @computed get slicedAllFolders() {
        return this.slicedFolders.concat(this.slicedSharedFolders);
    }

    @computed
    get numItemsPerFolderMap() {
        return this.folders.reduce((result, folder) => {
            let length = 0;
            const projectsPerFolder = projectStore.getProjectsByFolderIdFull(folder.id);
            if (projectsPerFolder) {
                length = projectsPerFolder.length;
            }
            result[folder.id] = length;
            return result;
        }, {});
    }

    @computed get slicedFolders() {
        return this.folders.filter(folder => folder.userId === getId() && !folder.shared).slice();
    }

    @computed get slicedSharedFolders() {
        return this.folders.filter(folder => folder.shared).slice();
    }

    @computed get filteredFolders() {
        return this.filterFoldersBySearchStr(this.slicedFolders, searchStore.getSearchStrFor(projectStore.searchState));
    }

    @computed get filteredSharedFolders() {
        return this.filterFoldersBySearchStr(this.slicedSharedFolders, searchStore.getSearchStrFor(projectStore.searchState));
    }

    filterFoldersBySearchStr = (folders, searchStr = '') => {
        const str = searchStr.toLowerCase();
        return folders.filter(folder => folder.name.toLowerCase().includes(str));
    };


    isFolderExist = folderId => !!this.findFolder(folderId);

    findFolder = folderId => this.folders.find(folder => folder.id === folderId);

    @action addFolder = async () => {
        mainStore.toggleProgressLoader(true);
        try {
            customEvent(events.CustomEvent, {category: category.AddFolder, action: this.editedFolder.shared ? actions.NewSharedFolder : ''});
            const folder = await request(`${FOLDER}`, {method: 'POST', body: this.editedFolder});
            runInAction(() => {
                this.folders.unshift(folder);
                this.setActiveFolder(folder.id);
            });
            // appStore.createAlertFromMessageId(AlertType.Success, 'addFolder.wasAdded');
            return folder;
        } catch (err) {
            throw err;
        } finally {
            runInAction(() => {
                mainStore.toggleProgressLoader(false);
            });
        }
    };

    @action toggleFolderShare = (folder) => action(async () => {
        try {
            folder.shared = !folder.shared;
            await request(`${FOLDER}/${folder.id}`, {method: 'POST', body: folder});
            runInAction(() => {
                appStore.createAlertFromMessageId(AlertType.Success, folder.shared ? 'editFolder.shared' : 'editFolder.unshared', {folderName: folder.name}, 5000);
            });
        } catch (err) {
            console.log(err);
            appStore.createAlertFromMessageId(AlertType.Error, 'editFolder.error');
        }
    });


    @action fetchFolders = async () => {
        if (this.foldersFetched) {
            return;
        }
        try {
            const folders = await request(`${FOLDER}`, {method: 'GET'});
            runInAction(() => {
                this.folders.replace(folders.sort((a, b) => Date.parse(b.created) - Date.parse(a.created)));
                this.foldersFetched = true;
            });
        } catch (err) {
            console.log(err);
        }
    };

    @action deleteFolder = async (folder) => {
        const folderIndex = this.folders.findIndex(element => element.id === folder.id);
        try {
            await request(`${FOLDER}/${folder.id}`, {method: 'DELETE'});
            await projectStore.deleteProjectByFolder(folder.id);
            runInAction(() => {
                this.folders.splice(folderIndex, 1);
            });
            appStore.createAlertFromMessageId('success', 'deleteFolder.deleted', {name: folder.name});
        } catch (err) {
            appStore.createAlertFromMessageId('error', 'deleteFolder.cantDelete', {error: err});
        }
    };

    @action editFolder = async () => {
        const folderIndex = this.folders.findIndex(element => element.id === this.editedFolder.id);
        try {
            await request(`${FOLDER}/${this.editedFolder.id}`, {method: 'POST', body: this.editedFolder});
            runInAction(() => {
                this.folders.splice(folderIndex, 1, this.editedFolder);
                // appStore.createAlertFromMessageId('success', 'editFolder.edited', 5000);
            });
        } catch (err) {
            appStore.createAlertFromMessageId('error', 'editFolder.error', {error: err});
        }
    };

    @action assignToFolder = async (folder, lesson) => {
        try {
            let msgId;
            if (folder.shared || folder.type === 'sharedLessons') {
                let sharedFolderId = null;
                if (folder.id) {
                    sharedFolderId = folder.id;
                }
                lesson.sharedFolderId = sharedFolderId;
                lesson.schoolShare = true;
                msgId = 'lessonsLibrary.card.shared';
            } else {
                let folderId = null;
                if (folder.id) {
                    folderId = folder.id;
                }
                lesson.folderId = folderId;
                msgId = 'lessonsLibrary.card.move';
            }

            if (lesson.userId !== getId() && !isSchoolAdmin()) {
                delete lesson.id;
                delete lesson.sharedFolderId;
                delete lesson.schoolShare;
                msgId = 'lessonsLibrary.card.add';
                lesson = {...lesson, ...getNewLessonParams()};
            }

            await projectStore.saveProject(lesson.name, lesson);

            appStore.createAlertFromMessageId(AlertType.Success, msgId, {lessonName: lesson.name});
        } catch (err) {
            throw err;
        } finally {
            runInAction(() => {
                projectStore.editedLesson = null;
            });
        }
    }
}

const folderStore = new FolderStore();
export default folderStore;

